import { useLiveQuery } from "dexie-react-hooks";
import { BarChart3, Boxes, HandCoins, Landmark, TrendingUp, WalletCards } from "lucide-react";
import { Card, Money, PageHeader } from "../components/ui";
import { db } from "../lib/db";

export function ReportsPage() {
  const dashboard = useLiveQuery(async () => (await db.meta.get("dashboard"))?.value as any, []);
  const products = useLiveQuery(() => db.products.toArray(), [], []);
  const money = useLiveQuery(() => db.money.toArray(), [], []);
  const debts = useLiveQuery(async () => ((await db.meta.get("debts"))?.value as Array<{ counterpartyType: "CUSTOMER" | "SUPPLIER"; balance: number }> | undefined) ?? [], [], []);

  const stockValue = products.reduce((sum, p) => sum + Math.round(p.stockQty * p.avgCost), 0);
  const cachedIncome = money.filter((m) => m.direction === "IN").reduce((sum, m) => sum + m.amount, 0);
  const cachedExpense = money.filter((m) => m.direction === "OUT").reduce((sum, m) => sum + m.amount, 0);
  const cachedReceivable = debts
    .filter((item) => item.counterpartyType === "CUSTOMER")
    .reduce((sum, item) => sum + Number(item.balance), 0);
  const cachedPayable = debts
    .filter((item) => item.counterpartyType === "SUPPLIER")
    .reduce((sum, item) => sum + Number(item.balance), 0);

  const income = dashboard?.finance?.cashIn ?? cachedIncome;
  const expense = dashboard?.finance?.cashOut ?? cachedExpense;
  const receivable = dashboard?.finance?.receivable ?? cachedReceivable;
  const payable = dashboard?.finance?.payable ?? cachedPayable;

  const reports = [
    { icon: TrendingUp, title: "Doanh thu & lợi nhuận", value: dashboard?.revenue ?? 0, note: (dashboard?.estimatedProfit ?? 0) > 0 ? `Lợi nhuận tạm tính ${(dashboard?.estimatedProfit ?? 0).toLocaleString("vi-VN")} ₫` : "Chưa có dữ liệu báo cáo lợi nhuận" },
    { icon: Boxes, title: "Nhập – Xuất – Tồn", value: stockValue, note: products.length > 0 ? `${products.length} sản phẩm đang quản lý` : "Chưa có sản phẩm" },
    { icon: BarChart3, title: "Hàng bán chạy", value: dashboard?.orderCount ?? 0, note: (dashboard?.orderCount ?? 0) > 0 ? "Số đơn ghi nhận hôm nay" : "Chưa có giao dịch hôm nay", count: true },
    { icon: HandCoins, title: "Phải thu khách hàng", value: receivable, note: receivable > 0 ? "Số tiền khách hàng còn phải thanh toán" : "Không có khoản phải thu" },
    { icon: HandCoins, title: "Phải trả nhà cung cấp", value: payable, note: payable > 0 ? "Số tiền cửa hàng còn phải thanh toán cho nhà cung cấp" : "Không có khoản phải trả" },
    { icon: WalletCards, title: "Dòng tiền hôm nay", value: income - expense, note: income > 0 || expense > 0 ? `Tiền vào ${income.toLocaleString("vi-VN")} · Tiền ra ${expense.toLocaleString("vi-VN")}` : "Chưa có dòng tiền ghi nhận hôm nay" },
    { icon: Landmark, title: "Thuế & nghĩa vụ", value: dashboard?.revenue ?? 0, note: "Chỉ hiển thị dữ liệu ghi nhận; không tự suy diễn nghĩa vụ thuế" },
  ];

  return (
    <div className="page-stack">
      <PageHeader back title="Báo cáo" description="Doanh thu, tồn kho, dòng tiền và công nợ được tách thành các chỉ tiêu độc lập" />
      <div className="report-grid">
        {reports.map(({ icon: Icon, title, value, note, count }) => (
          <Card key={title} className="report-card">
            <div className="report-icon"><Icon size={22} /></div>
            <span>{title}</span>
            {count ? <strong className="report-count">{value}</strong> : <Money value={value} className="money-large" />}
            <small>{note}</small>
          </Card>
        ))}
      </div>
    </div>
  );
}
